import React, { useEffect } from "react";
import { useState } from "react";
import { useHeaderEditor, isValidHeader } from "./headerEdit";
import { useOptionSelect } from "./optionSelect";
import { useTextEntry } from "./textEntry";
import { isAlphaNumeric, isAlphaNumericWithUnderScores, isValidURL } from "../utils/validation";
import { 
    FormWithMargin, 
    LightContainerWithMargin,
    DarkContainerWithMargin,
    ErrorContainerWithMargin
} from "../styles/styledComponents";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { base64Decode, base64Encode } from "../utils/base64";
import { ProxySettingsData, BLANK_HEADER, HeaderData } from "../utils/types";

export function useProxySettingsEditor(
    displayError: boolean,
    initialName="",
    initialMethod="",
    initialPolicy="",
    initialEndpoint="",
    initialHeaders: HeaderData[] = []
) { 
    // this bit will handle the header editor
    // we can have multiple headers, so we need to cycle through them
    const [headers, setHeaders] = useState<HeaderData[]>(initialHeaders);
    const [headerIndex, setHeaderIndex] = useState<number>(0);
    const showHeaderEditor = headers.length > 0;
    const headerInitialValues = showHeaderEditor ? headers[headerIndex % headers.length] : BLANK_HEADER;
    const {
        component: headerEditor,
        isValid: headerIsValid,
        headerData: headerDisplayValue,
        setHeader: setHeaderDisplayValue
    } = useHeaderEditor(
        displayError,
        headerInitialValues.destinationHeader,
        headerInitialValues.value,
        headerInitialValues.source
    );

    const cycleHeader = (index: number) => {
        // Update the header in the array
        const newHeaders = [...headers];
        newHeaders[headerIndex] = headerDisplayValue;
        setHeaders(newHeaders);
        const newIndex = (index + headers.length) % headers.length;
        setHeaderIndex(newIndex);
    };
    
    const addHeader = () => {
        const newHeaders = [...headers];
        // make sure current header is saved
        if(headerIsValid) {
            newHeaders[headerIndex] = headerDisplayValue;
        }
        newHeaders.push(BLANK_HEADER);
        setHeaders(newHeaders);
        setHeaderIndex(newHeaders.length - 1);
    };

    const removeHeader = () => {
        const newHeaders = [...headers];
        newHeaders.splice(headerIndex, 1);
        const newIndex = (headerIndex + newHeaders.length) % newHeaders.length;
        setHeaders(newHeaders);
        setHeaderIndex(newIndex);
    };

    useEffect(() => {
        if(headers.length) setHeaderDisplayValue(headers[headerIndex]);
    }, [headers, headerIndex])

    //TODO style this bad boi
    const cycleHeaderButtons = <>
        <Button onClick={() => cycleHeader(headerIndex - 1)}>Previous Header</Button>
        <Typography>Header {headerIndex + 1} / {headers.length}</Typography>
        <Button onClick={() => cycleHeader(headerIndex + 1)}>Next Header</Button>
    </>

    const removeHeaderButton = <Button onClick={() => removeHeader()}>
        Remove Header
    </Button>

    //Proxy name
    const {
        component: nameComponent,
        value: nameValue,
        setValue: setName,
        isValid: nameIsValid
    } = useTextEntry(initialName, displayError, 'Name', isAlphaNumeric);
    
    // http method 
    const {
        component: methodComponent,
        selectedOption: methodValue,
        setSelection: setMethod,
        isOptionSelected: methodIsValid
    } = useOptionSelect(
        "HTTP Method", 
        ["GET", "POST", "PUT", "PATCH"],
        displayError
    );

    // b2cPolicyString
    //TODO make this a selection dropdown from data pulled from the b2c tenant
    const {
        component: policyComponent,
        value: policyValue,
        setValue: setPolicy,
        isValid: policyIsValid
    } = useTextEntry(
        initialPolicy, displayError, 'B2C Policy', isAlphaNumericWithUnderScores
    );

    // endpoint URL
    const {
        component: endpointComponent,
        value: endpointValue,
        setValue: setEndpoint,
        isValid: endpointIsValid
    } = useTextEntry(initialEndpoint, displayError, 'Endpoint', isValidURL);

    const isValid = nameIsValid 
    && policyIsValid 
    && endpointIsValid 
    && methodIsValid
    && headers.every(isValidHeader);

    const setProxySettings = (proxySettings: ProxySettingsData) => {
        setName(proxySettings.name);
        setPolicy(proxySettings.policy);
        setMethod(proxySettings.method);
        setEndpoint(
            base64Decode(proxySettings.endpoint)
        );
        setHeaders(proxySettings.headers);
    };

    const proxySettingsData: ProxySettingsData = {
        name: nameValue,
        policy: policyValue,
        method: methodValue,
        endpoint: base64Encode(endpointValue),
        headers: headers
    };

    const headerInfoDisplay = headers.map((header, index) => {
        if(index === headerIndex) {
            header = headerDisplayValue;
        }
        if(!isValidHeader(header)) {
            return <ErrorContainerWithMargin key={index}>
                <Typography variant='h6'>Header {index + 1}</Typography>
                <Typography>Destination Header: {header.destinationHeader}</Typography>
                <Typography>Source: {header.source}</Typography>
                <Typography>Value: {header.value}</Typography>
            </ErrorContainerWithMargin>
        }
        if(index === headerIndex) {
            header = headerDisplayValue;
            return <LightContainerWithMargin key={index}>
            <Typography variant='h6'>Header {index + 1}</Typography>
            <Typography>Destination Header: {header.destinationHeader}</Typography>
            <Typography>Source: {header.source}</Typography>
            <Typography>Value: {header.value}</Typography>
        </LightContainerWithMargin>
        }
        return <DarkContainerWithMargin key={index}>
            <Typography variant='h6'>Header {index + 1}</Typography>
            <Typography>Destination Header: {header.destinationHeader}</Typography>
            <Typography>Source: {header.source}</Typography>
            <Typography>Value: {header.value}</Typography>
        </DarkContainerWithMargin>
    });

    const component = <>
        <FormWithMargin>
            <Typography>Edit Endpoint Configuration</Typography>
            {nameComponent}
            {policyComponent}
            {methodComponent}
            {endpointComponent}
            {showHeaderEditor && headerEditor}
            {showHeaderEditor && removeHeaderButton}
            {headers.length > 1 && cycleHeaderButtons}
            <Button onClick={addHeader}>Add Header</Button>
        </FormWithMargin>
        <FormWithMargin>
            <Typography>Current Headers</Typography>
            {headers.length > 0? headerInfoDisplay: 
            <LightContainerWithMargin>
                <Typography>No headers</Typography>
            </LightContainerWithMargin>
            }
        </FormWithMargin>
    </>

    return {
        component,
        isValid,
        proxySettingsData,
        setProxySettings
    }
}